/* eslint-disable complexity -- Delivery fallback and canonical crop precedence form one atomic media contract. */
/**
 * FmAssetImage — Registry-backed responsive image primitive.
 *
 * Pages reference media by registry key so no route carries a media URL; the
 * primitive owns aspect ratio, focal point and the responsive frame, and pages
 * pass assetKey, size and an alt override.
 *
 * Pages never pass src, fit, focalPoint, or srcSet.
 * All media and responsive behavior is DS-owned via the registry.
 * Raw URL rendering is delegated to FmAssetImageRenderer (DS-owned exempt surface).
 */

import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { FmAssetImageRenderer } from './FmAssetImageRenderer';
import { FmAssetLoopRenderer } from './FmAssetLoopRenderer';
import {
  getMediaAsset,
  getMediaDeliveryRevision,
  getLocalMediaAsset,
  subscribeMediaDelivery,
} from '@/shared/config/mediaRegistry';
import './FmAssetImage.css';

export interface FmAssetImageProps {
  /** Registry key for the media asset. MANDATORY. */
  assetKey: string;
  /** Optional override for alt text (defaults to registry alt) */
  altOverride?: string;
  /** Canonical placement selects a registry-owned crop; routes never pass focal geometry. */
  cropRole?: 'background' | 'split' | 'card' | 'entity-card';
  /** Optional class name (for theme/context-specific styling only) */
  className?: string;
  /** Optional callback when image loads */
  onLoad?: () => void;
  /** Optional callback on load error */
  onError?: () => void;
  'data-testid'?: string;
}

const NARROW_QUERY = '(max-width: 48em)';

/** Matches the stylesheet's mobile breakpoint, where a registered mobile rendition replaces the wide one. */
function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(NARROW_QUERY);
    const update = () => setNarrow(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return narrow;
}

/**
 * Returns CSS custom property for the aspect ratio (--fm-media-aspect-{kind}).
 */
function getAspectRatioVar(aspect: string): string {
  const map: Record<string, string> = {
    wide: '16 / 9',
    square: '1 / 1',
    portrait: '3 / 4',
    panorama: '21 / 9',
  };
  return map[aspect] ?? '16 / 9';
}

/**
 * Converts focal point string to CSS background-position.
 */

export const FmAssetImage: React.FC<FmAssetImageProps> = ({
  assetKey,
  altOverride,
  cropRole,
  className,
  onLoad,
  onError,
  'data-testid': dataTestId,
}) => {
  const deliveryRevision = useSyncExternalStore(
    subscribeMediaDelivery,
    getMediaDeliveryRevision,
    getMediaDeliveryRevision,
  );
  const deliveryIdentity = `${assetKey}:${deliveryRevision}`;
  const [failedDelivery, setFailedDelivery] = useState<string | null>(null);
  const deliveryFailed = failedDelivery === deliveryIdentity;
  const deliveredAsset = getMediaAsset(assetKey);
  const asset = deliveryFailed && deliveredAsset.delivery
    ? getLocalMediaAsset(assetKey)
    : deliveredAsset;
  const narrow = useNarrowViewport();
  const preservesPlacementAspect = cropRole === 'split' || cropRole === 'entity-card';
  // A split or entity card keeps its placement aspect on phones, so a portrait rendition would be cropped away.
  const mobileRendition =
    asset.mobileVariant && !preservesPlacementAspect ? getMediaAsset(asset.mobileVariant) : undefined;
  const rendition = narrow && mobileRendition ? mobileRendition : asset;
  const alt = altOverride ?? rendition.alt;
  const crop = cropRole ? asset.cropProfiles?.[cropRole] : undefined;
  const mobileCrop = mobileRendition
    ? { aspect: mobileRendition.aspect, focalPoint: mobileRendition.focalPoint ?? 'center', scale: 1 }
    : asset.cropProfiles?.mobile;
  const aspect = crop?.aspect ?? asset.aspect;
  const aspectRatio = getAspectRatioVar(aspect);
  const mobileAspect = preservesPlacementAspect
    ? crop?.aspect ?? aspect
    : mobileCrop?.aspect ?? aspect;
  const mobileFocalPoint = preservesPlacementAspect
    ? crop?.focalPoint ?? asset.focalPoint ?? 'center'
    : mobileCrop?.focalPoint ?? crop?.focalPoint ?? asset.focalPoint ?? 'center';
  const mobileScale = preservesPlacementAspect
    ? crop?.scale ?? 1
    : mobileCrop?.scale ?? crop?.scale ?? 1;

  return (
    <div
      className={`fm-asset-image fm-asset-image--${aspect} ${className ?? ''}`}
      style={{
        '--data-aspect-ratio': aspectRatio,
        '--data-aspect-ratio-mobile': getAspectRatioVar(mobileAspect),
        '--data-focal-point': crop?.focalPoint ?? asset.focalPoint ?? 'center',
        '--data-focal-point-mobile': mobileFocalPoint,
        '--data-media-scale': crop?.scale ?? 1,
        '--data-media-scale-mobile': mobileScale,
      } as React.CSSProperties}
      data-crop-role={cropRole}
      data-media-identity={asset.key}
      data-media-rendition={rendition.key}
      data-media-source={deliveryFailed && deliveredAsset.delivery
        ? 'local-fallback'
        : deliveredAsset.delivery
          ? 'delivered'
          : 'local-registry'}
      data-testid={dataTestId}
    >
      {/* Delegate raw URL rendering to DS-owned exempt renderers */}
      {rendition.kind === 'video' || rendition.kind === 'animation' ? (
        <FmAssetLoopRenderer key={rendition.key} asset={rendition} alt={alt} onLoad={onLoad} onError={onError} />
      ) : (
        <FmAssetImageRenderer
          asset={rendition}
          alt={alt}
          onLoad={onLoad}
          onError={() => {
            if (asset.delivery) setFailedDelivery(deliveryIdentity);
            onError?.();
          }}
        />
      )}
      {rendition.credit && (
        <div className="fm-asset-image__credit">
          <span className="fm-asset-image__credit-text">{rendition.credit}</span>
          {rendition.license && <span className="fm-asset-image__license">({rendition.license})</span>}
        </div>
      )}
    </div>
  );
};

FmAssetImage.displayName = 'FmAssetImage';
